import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Upload, Warning, CheckCircle, ArrowCounterClockwise } from "@phosphor-icons/react";

const STATUS_STYLES = {
  dry_run_ok: "bg-blue-50 border-blue-200 text-blue-900",
  inserted: "bg-green-50 border-green-200 text-green-900",
  blocked: "bg-amber-50 border-amber-200 text-amber-900",
  rolled_back: "bg-purple-50 border-purple-200 text-purple-900",
  nothing_to_rollback: "bg-gray-50 border-gray-200 text-gray-700",
};

export default function ImportHistoryTab() {
  const confirm = useConfirm();
  const [file, setFile] = useState(null);
  const [limit, setLimit] = useState(1);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (mode, opts = {}) => {
    if (!file) {
      toast.error("Please select an .xlsx file first.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      fd.append("limit", String(opts.limit ?? limit ?? 0));
      fd.append("confirm_all", opts.confirm_all ? "true" : "false");
      const { data } = await api.post("/admin/import-history", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.status === "inserted") {
        toast.success(`Inserted ${data.inserted_count} note(s).`);
      } else if (data.status === "dry_run_ok") {
        toast.success(`Dry-run OK — ${data.fresh_inserts_planned} would be inserted.`);
      } else {
        toast.message("Run blocked. See report below.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Import failed.";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  const runFullLive = async () => {
    const ok = await confirm({
      title: "Run full import?",
      message:
        "This will insert ALL eligible rows from the Excel into the production firm_notes collection. " +
        "Already-imported rows will be skipped automatically. You can undo via Rollback.",
      confirmLabel: "Run full import",
      tone: "danger",
    });
    if (!ok) return;
    await run("live", { limit: 0, confirm_all: true });
  };

  const runRollback = async () => {
    const ok = await confirm({
      title: "Rollback last import?",
      message:
        "This deletes EVERY note tagged with the current import_batch. " +
        "Only the bulk-imported historical rows are removed — your normal notes are untouched.",
      confirmLabel: "Delete imported rows",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/import-history/rollback");
      setResult(data);
      toast.success(
        data.status === "rolled_back"
          ? `Deleted ${data.deleted_count} rows.`
          : "Nothing tagged to rollback."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="import-history-tab">
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="text-lg font-semibold text-gray-900">Bulk Import Historical Notes</h2>
        <p className="mt-1 text-sm text-gray-500">
          Upload an .xlsx with columns:&nbsp;
          <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">
            firm_name, date, time, remark, duration_minutes, logged_by_email
          </code>
          . Always run <b>Dry-run</b> first. Imports are tagged with an{" "}
          <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">import_batch</code>{" "}
          so a one-click rollback is available.
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs text-gray-600">Excel file (.xlsx)</Label>
            <Input
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 h-9 text-xs"
              data-testid="import-file-input"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Row limit (0 = all)</Label>
            <Input
              type="number"
              value={limit}
              min={0}
              onChange={(e) => setLimit(Number(e.target.value) || 0)}
              className="mt-1 h-9 w-28 text-xs"
              data-testid="import-limit-input"
            />
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button
            onClick={() => run("dry_run")}
            disabled={busy || !file}
            variant="outline"
            data-testid="import-dryrun-btn"
          >
            <Upload size={14} className="mr-1.5" />
            Dry-run (no writes)
          </Button>
          <Button
            onClick={() => run("live")}
            disabled={busy || !file || limit <= 0}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="import-live-limited-btn"
          >
            <CheckCircle size={14} className="mr-1.5" />
            Live — first {limit} row(s)
          </Button>
          <Button
            onClick={runFullLive}
            disabled={busy || !file}
            className="bg-amber-600 text-white hover:bg-amber-700"
            data-testid="import-live-full-btn"
          >
            <Warning size={14} className="mr-1.5" />
            Live — full import
          </Button>
          <Button
            onClick={runRollback}
            disabled={busy}
            variant="outline"
            className="ml-auto text-red-700 hover:bg-red-50"
            data-testid="import-rollback-btn"
          >
            <ArrowCounterClockwise size={14} className="mr-1.5" />
            Rollback last import
          </Button>
        </div>
      </div>

      {result ? (
        <div
          className={`rounded-lg border p-5 ${STATUS_STYLES[result.status] || "bg-gray-50 border-gray-200"}`}
          data-testid="import-result-panel"
        >
          <div className="text-sm font-semibold uppercase tracking-wide">
            {result.status.replace(/_/g, " ")}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
            {"total_rows" in result && <Stat k="Total rows" v={result.total_rows} />}
            {"matched_firms" in result && <Stat k="Matched firms" v={result.matched_firms} />}
            {"fresh_inserts_planned" in result && (
              <Stat k="Planned inserts" v={result.fresh_inserts_planned} />
            )}
            {"inserted_count" in result && <Stat k="Inserted" v={result.inserted_count} />}
            {"duplicates_skipped" in result && (
              <Stat k="Duplicates skipped" v={result.duplicates_skipped} />
            )}
            {"deleted_count" in result && <Stat k="Deleted" v={result.deleted_count} />}
          </div>

          {result.user ? (
            <div className="mt-3 text-xs">
              <b>logged_by_email user:</b> {result.user.email} · {result.user.name} · {result.user.role}
            </div>
          ) : null}

          {result.unmatched_firms?.length ? (
            <div className="mt-3 text-xs">
              <b>Unmatched firm tokens:</b>{" "}
              <code className="rounded bg-white/60 px-1 py-0.5">
                {result.unmatched_firms.join(", ")}
              </code>
            </div>
          ) : null}

          {result.errors?.length ? (
            <div className="mt-3 text-xs">
              <b>Build errors (first 20):</b>
              <ul className="mt-1 list-disc pl-5">
                {result.errors.map((e, i) => (
                  <li key={`err-${e.excel_row}-${i}`}>
                    Excel row {e.excel_row}: {e.error}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {result.preview ? (
            <details className="mt-4">
              <summary className="cursor-pointer text-xs font-medium">
                Preview of first doc that would be / was inserted
              </summary>
              <pre className="mt-2 overflow-auto rounded bg-white/60 p-3 text-[11px] leading-snug">
                {JSON.stringify(result.preview, null, 2)}
              </pre>
            </details>
          ) : null}
        </div>
      ) : null}

      <SalesPocImporter />
    </div>
  );
}

// ---------- Sales-POC mapping importer ----------
function SalesPocImporter() {
  const confirm = useConfirm();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (mode) => {
    if (!file) {
      toast.error("Pick a Sales-POC .xlsx first.");
      return;
    }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const { data } = await api.post("/admin/import-sales-poc", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.status === "updated") toast.success(`Updated sales POC on ${data.firms_updated} firm(s).`);
      else if (data.status === "dry_run_ok") toast.success(`Dry-run OK — ${data.matched_firms} firms would be updated.`);
      else toast.message("Run blocked. See report below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sales POC import failed.");
    } finally {
      setBusy(false);
    }
  };

  const runRollback = async () => {
    const ok = await confirm({
      title: "Clear sales POC mapping?",
      message: "This clears `sales_poc` on every firm that was set by this batch. Manually edited firms are untouched.",
      confirmLabel: "Clear sales POCs",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/import-sales-poc/rollback");
      setResult(data);
      toast.success(data.status === "rolled_back" ? `Cleared on ${data.cleared_count} firms.` : "Nothing to rollback.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5" data-testid="sales-poc-importer">
      <h2 className="text-lg font-semibold text-gray-900">Bulk Import Sales POC Mapping</h2>
      <p className="mt-1 text-sm text-gray-500">
        Upload an .xlsx with columns:&nbsp;
        <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">Subdomain, Salespoc</code>.
        Spelling variants like "Maria Roopini", "Mara", "Sandeep Jha" are auto-normalised. Unknown POC names are added to the registry automatically (so they appear in dropdowns).
      </p>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-gray-600">Sales-POC file (.xlsx)</Label>
          <Input
            type="file"
            accept=".xlsx"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="mt-1 h-9 text-xs"
            data-testid="sales-poc-file-input"
          />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <Button onClick={() => run("dry_run")} disabled={busy || !file} variant="outline" data-testid="sales-poc-dryrun-btn">
          <Upload size={14} className="mr-1.5" /> Dry-run (no writes)
        </Button>
        <Button onClick={() => run("live")} disabled={busy || !file} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="sales-poc-live-btn">
          <CheckCircle size={14} className="mr-1.5" /> Live — apply mapping
        </Button>
        <Button onClick={runRollback} disabled={busy} variant="outline" className="ml-auto text-red-700 hover:bg-red-50" data-testid="sales-poc-rollback-btn">
          <ArrowCounterClockwise size={14} className="mr-1.5" /> Clear last batch
        </Button>
      </div>

      {result ? (
        <div className={`mt-4 rounded-lg border p-4 ${STATUS_STYLES[result.status] || "bg-gray-50 border-gray-200"}`} data-testid="sales-poc-result-panel">
          <div className="text-sm font-semibold uppercase tracking-wide">{(result.status || "").replace(/_/g, " ")}</div>
          <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
            {"total_rows" in result && <Stat k="Total rows" v={result.total_rows} />}
            {"matched_firms" in result && <Stat k="Matched firms" v={result.matched_firms} />}
            {"firms_updated" in result && <Stat k="Updated" v={result.firms_updated} />}
            {"firms_unchanged" in result && <Stat k="Unchanged" v={result.firms_unchanged} />}
            {"cleared_count" in result && <Stat k="Cleared" v={result.cleared_count} />}
          </div>
          {result.distinct_sales_pocs?.length ? (
            <div className="mt-3 text-xs">
              <b>Sales POCs detected:</b> {result.distinct_sales_pocs.join(", ")}
            </div>
          ) : null}
          {result.created_pocs?.length ? (
            <div className="mt-2 text-xs">
              <b>Auto-added to registry:</b> {result.created_pocs.join(", ")}
            </div>
          ) : null}
          {result.spelling_normalisations && Object.keys(result.spelling_normalisations).length ? (
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer font-medium">Spelling normalisations</summary>
              <ul className="mt-1 list-disc pl-5">
                {Object.entries(result.spelling_normalisations).map(([canonical, variants]) => (
                  <li key={canonical}>
                    <b>{canonical}</b> ← {variants.join(", ")}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          {result.unmatched_firms?.length ? (
            <div className="mt-2 text-xs">
              <b>Unmatched firms:</b>{" "}
              <code className="rounded bg-white/60 px-1 py-0.5">{result.unmatched_firms.join(", ")}</code>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{k}</div>
      <div className="text-base font-semibold">{v}</div>
    </div>
  );
}
